import * as React from 'react'

import { cn } from '@/lib/utils'

// ==================== Context ====================

interface ContextMenuContextValue {
  open: boolean
  setOpen: (open: boolean) => void
  position: { x: number; y: number }
  setPosition: (position: { x: number; y: number }) => void
}

const ContextMenuContext = React.createContext<ContextMenuContextValue | null>(null)

function useContextMenu() {
  const context = React.useContext(ContextMenuContext)
  if (!context) {
    throw new Error('useContextMenu must be used within a ContextMenu')
  }
  return context
}

// ==================== ContextMenu ====================

interface ContextMenuProps {
  children: React.ReactNode
}

export function ContextMenu({ children }: ContextMenuProps) {
  const [open, setOpen] = React.useState(false)
  const [position, setPosition] = React.useState({ x: 0, y: 0 })

  return (
    <ContextMenuContext.Provider value={{ open, setOpen, position, setPosition }}>
      {children}
    </ContextMenuContext.Provider>
  )
}

// ==================== ContextMenuTrigger ====================

interface ContextMenuTriggerProps {
  children: React.ReactNode
}

export function ContextMenuTrigger({ children }: ContextMenuTriggerProps) {
  const { setOpen, setPosition } = useContextMenu()

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault()
    setPosition({ x: e.clientX, y: e.clientY })
    setOpen(true)
  }

  return (
    <div onContextMenu={handleContextMenu}>
      {children}
    </div>
  )
}

// ==================== ContextMenuContent ====================

interface ContextMenuContentProps {
  children: React.ReactNode
  className?: string
}

export function ContextMenuContent({ children, className }: ContextMenuContentProps) {
  const { open, setOpen, position } = useContextMenu()
  const ref = React.useRef<HTMLDivElement>(null)

  // Close on click outside
  React.useEffect(() => {
    if (!open) {return}

    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [open, setOpen])

  if (!open) {return null}

  return (
    <div
      ref={ref}
      className={cn(
        'fixed z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md',
        'animate-in fade-in-0 zoom-in-95',
        className
      )}
      style={{
        left: position.x,
        top: position.y,
      }}
    >
      {children}
    </div>
  )
}

// ==================== ContextMenuItem ====================

interface ContextMenuItemProps {
  children: React.ReactNode
  onClick?: () => void
  disabled?: boolean
  className?: string
}

export function ContextMenuItem({
  children,
  onClick,
  disabled,
  className,
}: ContextMenuItemProps) {
  const { setOpen } = useContextMenu()

  const handleClick = () => {
    if (disabled) {return}
    onClick?.()
    setOpen(false)
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      className={cn(
        'relative flex w-full cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors',
        'hover:bg-accent hover:text-accent-foreground',
        'focus:bg-accent focus:text-accent-foreground',
        disabled && 'pointer-events-none opacity-50',
        className
      )}
    >
      {children}
    </button>
  )
}

// ==================== ContextMenuSeparator ====================

export function ContextMenuSeparator() {
  return <div className="-mx-1 my-1 h-px bg-muted" />
}
